import type { AnnonceDiffusion, Caracteristique, Diffusion, DiffusionStatusEnum, Localisation, Partenaire, PrixDetail } from '~/api/gen'
import { formatCurrency } from '~/utils/currency'

export interface CardDiffusionPartner {
  id: string
  /** Code produit réel de la diffusion (`Diffusion.code_produit`) — utilisé par les appels `relancer`. */
  codeProduit?: string
  logo?: string
  logoAlt?: string
  chipColor?: 'neutral' | 'success' | 'warning' | 'error' | 'info'
  selected?: boolean
  /** Statut de la diffusion (enum du contrat) ; undefined = jamais diffusé. */
  status?: DiffusionStatusEnum
  photosSent?: number
  diffusionDate?: string
  exclusionReason?: string
  listingUrl?: string
}

export interface DiffusionRow {
  id: string
  /** Identifiant numérique de l'annonce — requis par les endpoints `relancer`. */
  annonceId: number
  reference: string
  transactionType: string
  propertyType: string
  propertyCategory: string
  location: string
  price: string
  image: string
  selected: boolean
  partners: CardDiffusionPartner[]
  characteristics?: Caracteristique
  /** Lignes de l'infobulle du prix (vide = pas d'icône info sur la carte). */
  priceDetails: string[]
}

/**
 * Catalogue des métadonnées UI des partenaires (logos, packs), identifiées par `id`.
 * Les codes produit ne vivent PAS ici : ils viennent du référentiel API
 * `/partenaires/{officeOid}` (`Partenaire.code`), joint aux diffusions par annonce.
 */
export interface PartnerDef {
  id: 'immonot' | 'leboncoin' | 'seloger' | 'bienici'
  logo: string
  icon: string
  logoAlt: string
  packLabel?: string
  subscribe?: boolean
}

export const PARTNERS_CATALOG: PartnerDef[] = [
  {
    id: 'immonot',
    logo: '/images/logos/immonot.png',
    icon: '/images/logos/icons/immonot.png',
    logoAlt: 'Immonot',
    packLabel: 'Pack : 50 annonces',
  },
  {
    id: 'leboncoin',
    logo: '/images/logos/leboncoin.png',
    icon: '/images/logos/icons/leboncoin.png',
    logoAlt: 'Leboncoin',
    packLabel: 'Pack : 50 annonces',
  },
  {
    id: 'seloger',
    logo: '/images/logos/se-loger.png',
    icon: '/images/logos/icons/se-loger.png',
    logoAlt: 'SeLoger',
    packLabel: 'Pack : 10 annonces',
  },
  {
    id: 'bienici',
    logo: '/images/logos/bienici.png',
    icon: '/images/logos/icons/bienici.png',
    logoAlt: 'Bienici',
    packLabel: 'Pack : 10 annonces',
  },
]

/**
 * Correspondance `Diffusion.code_produit` (retourné par `/diffusions/{officeOid}`)
 * → id du catalogue partenaires. Seuls ces 3 partenaires sont gérés côté diffusion
 * à ce jour ; un `code_produit` absent de cette table (ex. IDEALISTA, PA-18) est
 * ignoré par `buildPartnersFromDiffusions`.
 */
const CODE_PRODUIT_TO_PARTNER_ID: Record<string, PartnerDef['id']> = {
  LEI: 'immonot',
  LBC: 'leboncoin',
  SLG: 'seloger',
  BIENI: 'bienici',
}

// Le backend relaie les codes bruts de la base annonces (snake_case) : le contrat
// les type en `string` libre, le mapping code → libellé se fait donc ici.

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  achat: 'Vente',
  location: 'Location',
  encheres_en_ligne: 'Enchères en ligne',
  encheres_classiques: 'Enchères classiques',
  viager: 'Viager',
  neuf: 'Neuf',
}

// Libellés alignés sur le référentiel backend (liquibase property_type.csv).
const BIEN_TYPE_LABELS: Record<string, string> = {
  maison: 'Maison',
  appartement: 'Appartement',
  terrain_a_batir: 'Terrain à bâtir',
  propriete: 'Propriété',
  garage_et_parking: 'Garage - Parking',
  bien_agricole: 'Bien agricole',
  immeuble: 'Immeuble',
  propriete_viticole: 'Propriété viticole',
  fonds_ou_murs_commerciaux: 'Fonds et/ou murs commerciaux',
  studio: 'Studio',
  t1: 'T1',
  t2: 'T2',
  t3: 'T3',
  t4: 'T4',
  t5: 'T5',
  terrain_a_vendre: 'Terrain à vendre',
  terrain_de_loisir_bois_etang: 'Terrain de loisirs - Bois - Etang',
  maison_neuve_et_terrain_a_vendre: 'Maison neuve et terrain à vendre',
  autre: 'Autre',
}

// Filet de sécurité pour un code absent des mappings : "espace_pro" → "Espace pro".
function humanizeCode(code: string): string {
  const label = code.replaceAll('_', ' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export function transactionTypeLabel(code?: string): string {
  return code ? TRANSACTION_TYPE_LABELS[code] ?? humanizeCode(code) : ''
}

export function bienTypeLabel(code?: string): string {
  return code ? BIEN_TYPE_LABELS[code] ?? humanizeCode(code) : ''
}

// ---- Filtres de la sidebar de recherche (client side) ----

/** Critères de la sidebar. Les listes vides / chaînes vides = critère inactif. */
export interface DiffusionFilters {
  reference: string
  localite: string
  /** Libellés affichés (items de `TRANSACTION_FILTER_ITEMS`). */
  transaction: string[]
  /** Ids du catalogue partenaires (`PARTNERS_CATALOG`). */
  partenaire: string[]
  /** Groupes de statut sélectionnés (items de `STATUT_FILTER_ITEMS`). */
  statut: StatutFilterItem[]
}

/** Items du filtre transaction : les libellés mappés depuis l'enum backend. */
export const TRANSACTION_FILTER_ITEMS: string[] = [...new Set(Object.values(TRANSACTION_TYPE_LABELS))]

/** Les 6 statuts du contrat rabattus sur les 3 groupes proposés au filtre. */
export const STATUT_FILTER_ITEMS = ['Diffusé', 'En cours', 'Non diffusé'] as const

export type StatutFilterItem = (typeof STATUT_FILTER_ITEMS)[number]

const STATUT_FILTER_MATCHERS: Record<StatutFilterItem, (status?: DiffusionStatusEnum) => boolean> = {
  'Diffusé': status => status === 'publie',
  'En cours': status => status === 'en_attente' || status === 'en_attente_retry' || status === 'en_cours',
  // `undefined` = jamais diffusé (partenaire sans diffusion).
  'Non diffusé': status => status === undefined || status === 'erreur' || status === 'depublie',
}

// Comparaison insensible à la casse et aux accents ("bordeaux" trouve "Bordeaux").
const DIACRITICS_RE = /[\u0300-\u036F]/g

function normalizeSearch(value: string): string {
  return value.normalize('NFD').replace(DIACRITICS_RE, '').toLowerCase()
}

// Une annonce matche le filtre statut si l'un de ses partenaires est dans l'un des
// groupes sélectionnés. Une annonce sans aucune diffusion compte comme « Non diffusé ».
function rowMatchesStatut(row: DiffusionRow, statuts: StatutFilterItem[]): boolean {
  const matchers = statuts.map(s => STATUT_FILTER_MATCHERS[s])
  if (!row.partners.length)
    return statuts.includes('Non diffusé')
  return row.partners.some(p => matchers.some(matches => matches(p.status)))
}

/** Applique les critères de la sidebar au listing (tous les critères actifs en ET). */
export function filterDiffusionRows(rows: DiffusionRow[], filters: DiffusionFilters): DiffusionRow[] {
  const reference = normalizeSearch(filters.reference.trim())
  // `localite` est une localité choisie dans la liste des suggestions (format `codePostal nom`,
  // identique à celui de `row.location` construit par `formatLocalisation`) : comparaison exacte,
  // pas une recherche texte libre.
  const localite = normalizeSearch(filters.localite.trim())
  return rows.filter((row) => {
    if (reference && !normalizeSearch(row.reference).includes(reference))
      return false
    if (localite && normalizeSearch(row.location) !== localite)
      return false
    if (filters.transaction.length && !filters.transaction.includes(row.transactionType))
      return false
    if (filters.partenaire.length && !row.partners.some(p => filters.partenaire.includes(p.id)))
      return false
    if (filters.statut.length && !rowMatchesStatut(row, filters.statut))
      return false
    return true
  })
}

// Les 6 statuts du contrat sont rabattus sur les couleurs UI de la maquette.
type BadgeColor = 'neutral' | 'success' | 'warning' | 'error' | 'info'

const STATUS_LABELS: Record<DiffusionStatusEnum, string> = {
  en_attente: 'En cours',
  en_attente_retry: 'En cours',
  en_cours: 'En cours',
  publie: 'Publié',
  erreur: 'Non publiée',
  depublie: 'Non publiée',
}

const STATUS_BADGES: Record<DiffusionStatusEnum, BadgeColor> = {
  en_attente: 'info', // « En cours » → bleu (ui-info)
  en_attente_retry: 'info',
  en_cours: 'info',
  publie: 'success',
  erreur: 'error', // pastille rouge
  depublie: 'neutral',
}

export function diffusionStatusLabel(status?: DiffusionStatusEnum): string {
  return status ? STATUS_LABELS[status] : 'Non publiée'
}

export function diffusionStatusBadge(status?: DiffusionStatusEnum): BadgeColor {
  return status ? STATUS_BADGES[status] : 'neutral'
}

// Publié ou en cours de publication → le bouton « Publier » est désactivé.
export function isDiffusionPublished(status?: DiffusionStatusEnum): boolean {
  return status === 'publie' || status === 'en_attente' || status === 'en_attente_retry' || status === 'en_cours'
}

// Publié, en cours ou en erreur → la case du partenaire arrive pré-cochée au
// chargement. Seuls « dépublié » et « jamais diffusé » démarrent décochés.
export function isDiffusionPreChecked(status?: DiffusionStatusEnum): boolean {
  return isDiffusionPublished(status) || status === 'erreur'
}

/**
 * Libellé du pack souscrit par l'office chez un partenaire, à partir du
 * référentiel API (quota et type de diffusion viennent de la base — table
 * produit_organisation). `undefined` si le pack n'est pas souscrit / inconnu.
 */
export function partnerPackLabel(partenaire?: Partenaire): string | undefined {
  if (partenaire?.quotaAnnonces != null)
    return `Pack : ${partenaire.quotaAnnonces} annonces`
  if (partenaire?.typeDiffusion === 'UNLIMITED')
    return 'Pack : illimité'
  return undefined
}

/**
 * Nombre d'annonces diffusées par partenaire (id du catalogue → compteur), pour
 * le carrousel de la page. Une annonce compte pour un partenaire si sa diffusion
 * est publiée, en cours ou en erreur — même règle que le pré-cochage des cases.
 */
export function countDiffusedAnnonces(rows: DiffusionRow[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const row of rows) {
    for (const partner of row.partners) {
      if (isDiffusionPreChecked(partner.status))
        counts[partner.id] = (counts[partner.id] ?? 0) + 1
    }
  }
  return counts
}

// Nombre de partenaires en erreur sur une annonce (alimente les bandeaux « N erreurs en cours »).
export function rowErrorCount(row: DiffusionRow): number {
  return row.partners.filter(p => p.status === 'erreur').length
}

// Une annonce a une diffusion dès qu'au moins un partenaire lui est associé
// (seuls les partenaires ayant une diffusion existante sont construits, cf.
// buildPartnersFromDiffusions) — sert à la vue « Annonces diffusées ».
export function hasDiffusion(row: DiffusionRow): boolean {
  return row.partners.length > 0
}

// Annonces diffusées en premier, jamais diffusées en dernier ; ordre stable au sein
// de chaque groupe (préserve l'ordre reçu de l'API/des filtres).
export function sortRowsByDiffusion(rows: DiffusionRow[]): DiffusionRow[] {
  const diffused: DiffusionRow[] = []
  const notDiffused: DiffusionRow[] = []
  for (const row of rows)
    (hasDiffusion(row) ? diffused : notDiffused).push(row)
  return diffused.concat(notDiffused)
}

// Libellé accessible du bandeau d'erreur (singulier/pluriel).
export function errorBannerLabel(count: number): string {
  return `${count} ${count > 1 ? 'erreurs' : 'erreur'} en cours`
}

/**
 * Construit les partenaires d'une annonce à partir de ses diffusions API.
 *
 * Seuls les partenaires présents dans `diffusions` sont affichés : chaque
 * `code_produit` est résolu directement via `CODE_PRODUIT_TO_PARTNER_ID`,
 * puis enrichi des métadonnées UI du catalogue. Une diffusion au code inconnu est
 * ignorée. Le `status` est la seule source de vérité de la pastille : `active`
 * n'entre pas en jeu (en prod, des diffusions `publie` portent `active: false`).
 *
 * Un partenaire déjà diffusé (publié, en cours ou en erreur) arrive pré-coché :
 * la case reflète l'état de diffusion courant, et seul un écart avec cet état
 * initial active le bouton « Envoyer » (cf. useDiffusionChanges).
 */
function buildPartnersFromDiffusions(diffusions: Diffusion[] = []): CardDiffusionPartner[] {
  const partners = new Map<string, CardDiffusionPartner>()
  for (const diffusion of diffusions) {
    const partnerId = diffusion.code_produit ? CODE_PRODUIT_TO_PARTNER_ID[diffusion.code_produit] : undefined
    const partner = PARTNERS_CATALOG.find(p => p.id === partnerId)
    if (!partner) {
      if (import.meta.dev)
        console.warn(`Diffusion ignorée : code produit inconnu du référentiel partenaires (${diffusion.code_produit})`)
      continue
    }
    if (partners.has(partner.id))
      continue
    const status = diffusion.status
    partners.set(partner.id, {
      id: partner.id,
      codeProduit: diffusion.code_produit,
      logo: partner.icon,
      logoAlt: partner.logoAlt,
      chipColor: diffusionStatusBadge(status),
      selected: isDiffusionPreChecked(status),
      status,
      // Détail affiché dans le slideover (DiffusionPartenaire) — issu de la base.
      photosSent: diffusion.nb_photos_envoyees,
      diffusionDate: formatDateTime(diffusion.date_diffusion),
      exclusionReason: diffusion.message_erreur,
      listingUrl: diffusion.url_annonce,
    })
  }
  // Ordre des logos stable entre cartes : celui du catalogue, pas celui de l'API.
  return PARTNERS_CATALOG.flatMap(p => partners.get(p.id) ?? [])
}

/**
 * Formate une localisation en "31000 Toulouse" (code postal + commune), comme sur la maquette.
 */
function formatLocalisation(localisation?: Localisation): string {
  return [localisation?.codePostal, localisation?.commune].filter(Boolean).join(' ')
}

// Libellés de la partie supportant les honoraires (valeurs libres de la base annonces).
const CHARGE_NEGOCIATION_LABELS: Record<string, string> = {
  acquereur: 'charge acquéreur',
  vendeur: 'charge vendeur',
}

/**
 * Lignes de l'infobulle du prix, dans l'ordre d'affichage. Seuls les champs
 * renseignés par l'API produisent une ligne ; liste vide = pas d'icône info.
 */
export function buildPriceDetails(detail?: PrixDetail): string[] {
  if (!detail)
    return []
  const lines: string[] = []
  if (detail.prix_net_vendeur != null)
    lines.push(`Prix net vendeur : ${formatCurrency(detail.prix_net_vendeur)}`)
  if (detail.honoraires_negociation != null) {
    const charge = detail.charge_negociation ? CHARGE_NEGOCIATION_LABELS[detail.charge_negociation] : undefined
    lines.push(`Honoraires de négociation : ${formatCurrency(detail.honoraires_negociation)}${charge ? ` (${charge})` : ''}`)
  }
  if (detail.loyer_mensuel_hors_charges != null)
    lines.push(`Loyer mensuel hors charges : ${formatCurrency(detail.loyer_mensuel_hors_charges)}`)
  if (detail.montant_charges != null)
    lines.push(`Charges mensuelles : ${formatCurrency(detail.montant_charges)}`)
  if (detail.depot_garantie != null)
    lines.push(`Dépôt de garantie : ${formatCurrency(detail.depot_garantie)}`)
  return lines
}

export function mapAnnoncesToRows(annonces: AnnonceDiffusion[] = []): DiffusionRow[] {
  return annonces.map((annonce, index) => {
    const partners = buildPartnersFromDiffusions(annonce.diffusions)
    return {
      id: String(annonce.id ?? `row-${index}`),
      annonceId: annonce.id ?? -1,
      reference: annonce.ref_client ?? '',
      transactionType: transactionTypeLabel(annonce.type_transaction),
      propertyType: bienTypeLabel(annonce.type_bien),
      // Le contrat ne porte plus de sous-nature : on affiche les types secondaires.
      propertyCategory: annonce.liste_type_bien?.filter(t => t !== annonce.type_bien).map(bienTypeLabel).join(', ') ?? '',
      location: formatLocalisation(annonce.localisation),
      price: annonce.prix != null ? formatCurrency(annonce.prix) : '',
      image: annonce.photos?.[0]?.url ?? '/images/annonces/img-1.jpg',
      // Cohérent avec onRowPartnersChange : la ligne est cochée dès qu'un partenaire l'est.
      selected: partners.some(p => p.selected),
      partners,
      characteristics: annonce.caracteristiques,
      priceDetails: buildPriceDetails(annonce.detail_prix),
    }
  })
}

// ---- Suivi des modifications de sélection (bouton « Envoyer ») ----

/** Photographie de la sélection : id de ligne → ids des partenaires cochés. */
export type DiffusionSelectionSnapshot = Map<string, Set<string>>

export function snapshotSelection(rows: DiffusionRow[]): DiffusionSelectionSnapshot {
  return new Map(rows.map(row => [
    row.id,
    new Set(row.partners.filter(p => p.selected).map(p => p.id)),
  ]))
}

/** Écart de sélection d'une annonce par rapport à la référence (chargement de la page). */
export interface DiffusionRowChanges {
  row: DiffusionRow
  /** Partenaires cochés depuis le chargement → à diffuser. */
  checked: Set<string>
  /** Partenaires décochés depuis le chargement → à dépublier. */
  unchecked: Set<string>
}

/** Ne retourne que les annonces dont la sélection diffère de la référence. */
export function diffSelection(rows: DiffusionRow[], baseline: DiffusionSelectionSnapshot): DiffusionRowChanges[] {
  const changes: DiffusionRowChanges[] = []
  for (const row of rows) {
    const initial = baseline.get(row.id) ?? new Set<string>()
    const current = new Set(row.partners.filter(p => p.selected).map(p => p.id))
    const checked = new Set([...current].filter(id => !initial.has(id)))
    const unchecked = new Set([...initial].filter(id => !current.has(id)))
    if (checked.size || unchecked.size)
      changes.push({ row, checked, unchecked })
  }
  return changes
}
